import axios, { AxiosResponse, isAxiosError } from "axios";
import { Paginated, RequestError, setFile } from "./api";

const url = `${process.env.REACT_APP_URL}/oficios`;

export interface Oficio {
  id: number;
  idBarragem: number;
  status: boolean;
  tipo?: string;
  obs?: string;
  dataCadastro: Date;
  dataModificacao?: Date;
  idUpdate: string;
  file?: string;
  file_size?: number;
}

const add = async (
  idBarragem: number,
  oficio: Oficio
): Promise<Oficio | RequestError> => {
  try {
    const data: AxiosResponse<Oficio> = await axios.post(
      `${url}/${idBarragem}`,
      oficio
    );

    data.data.dataCadastro = new Date(data.data.dataCadastro);
    if (data.data.dataModificacao)
      data.data.dataModificacao = new Date(data.data.dataCadastro);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message:
          error.response?.data.message ?? "Erro ao adicionar este oficio",
      };
    } else {
      throw error;
    }
  }
};

const getAll = async (
  idBarragem: number,
  page: number,
  perPage: number
): Promise<Paginated<Oficio> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<Oficio>> = await axios.get(
      `${url}/all/${idBarragem}/${page}/${perPage}`
    );

    data.data.items = data.data.items.map((val) => {
      val.dataCadastro = new Date(val.dataCadastro);
      if (val.dataModificacao) val.dataModificacao = new Date(val.dataCadastro);
      return val;
    });

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao obter oficio",
      };
    } else {
      throw error;
    }
  }
};

const getTrash = async (
  idBarragem: number,
  page: number,
  perPage: number
): Promise<Paginated<Oficio> | RequestError> => {
  try {
    const data: AxiosResponse<Paginated<Oficio>> = await axios.get(
      `${url}/trash/${idBarragem}/${page}/${perPage}`
    );

    data.data.items = data.data.items.map((val) => {
      val.dataCadastro = new Date(val.dataCadastro);
      if (val.dataModificacao) val.dataModificacao = new Date(val.dataCadastro);
      return val;
    });

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao obter oficio",
      };
    } else {
      throw error;
    }
  }
};

const getById = async (idOficio: number): Promise<Oficio | RequestError> => {
  try {
    const data: AxiosResponse<Oficio> = await axios.get(`${url}/${idOficio}`);

    data.data.dataCadastro = new Date(data.data.dataCadastro);
    if (data.data.dataModificacao)
      data.data.dataModificacao = new Date(data.data.dataCadastro);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao obter oficio",
      };
    } else {
      throw error;
    }
  }
};

const update = async (
  oficio: Oficio,
  file?: File,
  onProgess?: (percent: number) => void
): Promise<Oficio | RequestError> => {
  try {
    const data: AxiosResponse<Oficio> = await axios.put(`${url}/${oficio.id}`, {
      ...oficio,
    });

    if (file) {
      const updateFile = await setFile<Oficio>(
        `${url}/file_upload_parts/${data.data.id}`,
        `${url}/file_upload_complete/${data.data.id}`,
        file,
        onProgess,
      );
      if ("error" in updateFile) return updateFile;
    }

    data.data.dataCadastro = new Date(data.data.dataCadastro);
    if (data.data.dataModificacao)
      data.data.dataModificacao = new Date(data.data.dataCadastro);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao atualizar oficio",
      };
    } else {
      throw error;
    }
  }
};

const remove = async (idOficio: number): Promise<Oficio | RequestError> => {
  try {
    const data: AxiosResponse<Oficio> = await axios.delete(
      `${url}/${idOficio}`
    );

    data.data.dataCadastro = new Date(data.data.dataCadastro);
    if (data.data.dataModificacao)
      data.data.dataModificacao = new Date(data.data.dataCadastro);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao atualizar oficio",
      };
    } else {
      throw error;
    }
  }
};

const restore = async (idOficio: number): Promise<Oficio | RequestError> => {
  try {
    const data: AxiosResponse<Oficio> = await axios.put(
      `${url}/restore/${idOficio}`
    );

    data.data.dataCadastro = new Date(data.data.dataCadastro);
    if (data.data.dataModificacao)
      data.data.dataModificacao = new Date(data.data.dataCadastro);

    return data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Treinamento Error",
        message: error.response?.data.message ?? "Erro ao atualizar oficio",
      };
    } else {
      throw error;
    }
  }
};

export const ApiOficio = {
  add,
  remove,
  update,
  getAll,
  getById,
  restore,
  getTrash,
};
